/**
 * Customer Terms Service
 *
 * Provides access to client payment terms and the token resolution logic used
 * by the Terms & Conditions merge field picker (Phase 4).
 *
 * getCustomerTerms calls the backend endpoint:
 *   GET /api/billing/customer-terms?customerCode={code}&companyId={companyId}
 * Falls back to mock data when the API is not available.
 */

import { getApiBaseUrl } from '@/lib/data-service';

// ============================================================================
// TYPES
// ============================================================================

/**
 * Payment terms for a client, as stored in the client record.
 */
export interface CustomerTerms {
  /** Client ID these terms belong to. */
  clientId: string | number;

  /** Short code, e.g. "NET30", "NET60", "IMMEDIATE". */
  code: string;

  /** Human-readable description, e.g. "Net 30 Days". */
  description: string;

  /** Number of days until payment is due. */
  dueDays: number;

  /** Optional free-text notes about these terms. */
  notes?: string;
}

/**
 * A single merge token definition — used by the T&C merge field picker (Phase 4).
 */
export interface MergeToken {
  /** The raw token string inserted into the template, e.g. `{client.paymentTermsDays}`. */
  token: string;

  /** Friendly label shown in the builder UI, e.g. `[Payment Terms Days]`. */
  label: string;

  /** Category used to group tokens in the picker dropdown. */
  category: 'client' | 'invoice' | 'project' | 'company';

  /** Short description of what this token resolves to. */
  description: string;
}

/**
 * The context object used to resolve tokens in the T&C field.
 * Populated from available data when rendering the invoice preview or wizard.
 */
export interface TokenContext {
  client?: {
    name?: string;
    paymentTermsDays?: number;
    paymentTermsDescription?: string;
    paymentTermsCode?: string;
  };
  invoice?: {
    date?: string;
    dueDate?: string;
  };
  project?: {
    name?: string;
  };
  company?: {
    name?: string;
  };
}

// ============================================================================
// MOCK DATA (fallback when API is unavailable)
// ============================================================================

const MOCK_CUSTOMER_TERMS: CustomerTerms[] = [
  {
    clientId: 1,
    code: 'NET30',
    description: 'Net 30 Days',
    dueDays: 30,
    notes: 'Standard payment terms for most clients.',
  },
  {
    clientId: 2,
    code: 'NET60',
    description: 'Net 60 Days',
    dueDays: 60,
    notes: 'Extended terms for enterprise clients.',
  },
  {
    clientId: 3,
    code: 'NET14',
    description: 'Net 14 Days',
    dueDays: 14,
    notes: 'Short-cycle billing for retainer clients.',
  },
  {
    clientId: 4,
    code: 'IMMEDIATE',
    description: 'Due on Receipt',
    dueDays: 0,
    notes: 'Payment due immediately on invoice receipt.',
  },
];

// ============================================================================
// ALL AVAILABLE MERGE TOKENS
// This list is the single source of truth for Phase 4 (T&C Merge Field Picker).
// See PRD Section 12.2 for the full specification.
// ============================================================================

export const ALL_MERGE_TOKENS: MergeToken[] = [
  // Client
  {
    token: '{client.paymentTermsDays}',
    label: '[Payment Terms Days]',
    category: 'client',
    description: 'Number of days until payment is due (e.g. 30)',
  },
  {
    token: '{client.paymentTermsDescription}',
    label: '[Payment Terms Description]',
    category: 'client',
    description: 'Human-readable payment terms (e.g. Net 30 Days)',
  },
  {
    token: '{client.paymentTermsCode}',
    label: '[Payment Terms Code]',
    category: 'client',
    description: 'Short payment terms code (e.g. NET30)',
  },
  {
    token: '{client.name}',
    label: '[Client Name]',
    category: 'client',
    description: 'The name of the client being invoiced',
  },
  // Invoice
  {
    token: '{invoice.date}',
    label: '[Invoice Date]',
    category: 'invoice',
    description: 'The date the invoice was issued',
  },
  {
    token: '{invoice.dueDate}',
    label: '[Due Date]',
    category: 'invoice',
    description: 'The date payment is due',
  },
  // Project
  {
    token: '{project.name}',
    label: '[Project Name]',
    category: 'project',
    description: 'The name of the project being invoiced',
  },
  // Company
  {
    token: '{company.name}',
    label: '[Company Name]',
    category: 'company',
    description: 'Your company name as entered in Company Details',
  },
];

// ============================================================================
// COMPANY ID (set by app bootstrap for API calls)
// ============================================================================

let _companyId: number | null = null;

export function setCustomerTermsCompanyId(id: number | null): void {
  _companyId = id;
}

// ============================================================================
// READ
// ============================================================================

/**
 * Returns the payment terms for a given client.
 * Returns undefined if no terms are found for the client.
 *
 * Backend: GET /api/billing/customer-terms?customerCode={customerCode}&companyId={companyId}
 * Falls back to mock data when the API is unavailable.
 */
export async function getCustomerTerms(
  clientId: string | number,
  customerCode?: string,
): Promise<CustomerTerms | undefined> {
  // Try the API if we have the required parameters
  if (customerCode && _companyId) {
    try {
      const params = new URLSearchParams({
        customerCode,
        companyId: String(_companyId),
      });
      const response = await fetch(`${getApiBaseUrl()}/billing/customer-terms?${params}`, {
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        return {
          clientId,
          code: data.code ?? data.paymentTermsCode ?? '',
          description: data.description ?? data.paymentTermsDescription ?? '',
          dueDays: data.dueDays ?? data.paymentTermsDays ?? 0,
          notes: data.notes ?? '',
        };
      }
    } catch {
      // Fall through to mock data
    }
  }

  // Fallback to mock data
  const numId = typeof clientId === 'string' ? parseInt(clientId, 10) : clientId;
  return MOCK_CUSTOMER_TERMS.find((t) => t.clientId === numId);
}

/**
 * Returns all available merge tokens for the T&C field picker.
 * This is a static list — no API call needed.
 */
export function getMergeTokens(): MergeToken[] {
  return ALL_MERGE_TOKENS;
}

// ============================================================================
// TOKEN RESOLUTION
// ============================================================================

/**
 * Resolves all merge tokens in a T&C content string using the provided context.
 *
 * - In the **template builder preview** (no client loaded), tokens remain as
 *   their friendly placeholder labels (e.g. `[Payment Terms Days]`).
 * - In the **invoice wizard** (client and project data available), tokens are
 *   replaced with real values.
 *
 * Handles both raw token format (`{client.paymentTermsDays}`) and friendly
 * label format (`[Payment Terms Days]`) so that content saved in either format
 * is resolved correctly.
 *
 * The context object is populated from real API data (customer terms, project,
 * company). No API call is needed for this function itself — it is a pure
 * transformation.
 */
export function resolveInvoiceTokens(content: string, context: TokenContext): string {
  if (!content) return content;

  let resolved = content;

  for (const token of ALL_MERGE_TOKENS) {
    const value = resolveTokenValue(token, context);

    if (value !== null) {
      // Replace raw token format: {client.paymentTermsDays}
      resolved = resolved.replaceAll(token.token, value);
      // Replace friendly label format: [Payment Terms Days]
      resolved = resolved.replaceAll(token.label, value);
    }
    // If value is null (context not available), leave the friendly label in place
    // so the builder preview shows readable placeholders.
  }

  return resolved;
}

/**
 * Returns the resolved value for a single token given the context,
 * or null if the required context data is not available.
 */
function resolveTokenValue(token: MergeToken, context: TokenContext): string | null {
  switch (token.token) {
    case '{client.paymentTermsDays}':
      return context.client?.paymentTermsDays != null
        ? String(context.client.paymentTermsDays)
        : null;

    case '{client.paymentTermsDescription}':
      return context.client?.paymentTermsDescription ?? null;

    case '{client.paymentTermsCode}':
      return context.client?.paymentTermsCode ?? null;

    case '{client.name}':
      return context.client?.name ?? null;

    case '{invoice.date}':
      return context.invoice?.date ?? null;

    case '{invoice.dueDate}':
      return context.invoice?.dueDate ?? null;

    case '{project.name}':
      return context.project?.name ?? null;

    case '{company.name}':
      return context.company?.name ?? null;

    default:
      return null;
  }
}
